// ============================================================
// EVENT STREAM PROVIDER — Fase 10.19
// Abstração de streaming de eventos
// Inicialmente: Supabase Realtime
// Preparado para: Kafka, NATS, Redis Streams
// ============================================================
import { supabase } from '@/lib/supabase'

export type StreamProvider = 'supabase' | 'kafka' | 'nats' | 'redis_streams'
export type EventHandler<T = unknown> = (event: T) => void

export interface StreamSubscription {
  unsubscribe: () => void
}

export interface EventStreamConfig {
  provider:  StreamProvider
  topic:     string          // table name (supabase) | topic (kafka/nats) | stream key (redis)
  filter?:   Record<string, string>
  handler:   EventHandler
}

// ─── Active provider (runtime-configurable) ──────────────────
let activeProvider: StreamProvider = 'supabase'

export function setStreamProvider(p: StreamProvider): void {
  activeProvider = p
}

export function getStreamProvider(): StreamProvider {
  return activeProvider
}

// ─── Subscribe ────────────────────────────────────────────────
export function subscribeStream(cfg: EventStreamConfig): StreamSubscription {
  switch (activeProvider) {
    case 'supabase':
      return subscribeSupabase(cfg)
    case 'kafka':
    case 'nats':
    case 'redis_streams':
      // Future: connect to external broker
      console.warn(`[EventStream] Provider '${activeProvider}' not yet implemented — falling back to Supabase`)
      return subscribeSupabase(cfg)
    default:
      return { unsubscribe: () => {} }
  }
}

// ─── Supabase Realtime implementation ────────────────────────
function subscribeSupabase(cfg: EventStreamConfig): StreamSubscription {
  const channelName = `stream_${cfg.topic}_${Date.now()}`

  const filterObj: Record<string, string> = { event: 'INSERT', schema: 'public', table: cfg.topic }
  if (cfg.filter) {
    // Build postgres_changes filter string
    const filters = Object.entries(cfg.filter)
      .map(([k, v]) => `${k}=eq.${v}`)
    if (filters.length > 0) filterObj.filter = filters[0]   // Supabase supports single filter
  }

  const channel = supabase
    .channel(channelName)
    .on('postgres_changes' as any, filterObj, (payload: any) => {
      if (payload.new) cfg.handler(payload.new)
    })
    .subscribe()

  return {
    unsubscribe: () => channel.unsubscribe(),
  }
}

// ─── Publish (local event bus bridge) ────────────────────────
export function publishStream(
  topic:   string,
  payload: unknown,
): void {
  // For Supabase: writes go directly to DB (triggers Realtime)
  // For external brokers: would publish to broker
  console.debug(`[EventStream] Publish to ${topic}:`, payload)
}

// ─── Multi-topic fan-out ──────────────────────────────────────
export function subscribeMultiple(
  topics:  string[],
  obraId:  string,
  handler: EventHandler,
): StreamSubscription[] {
  return topics.map(topic =>
    subscribeStream({ provider: activeProvider, topic, filter: { obra_id: obraId }, handler }),
  )
}

// ─── Health check ─────────────────────────────────────────────
export async function checkStreamHealth(): Promise<{ ok: boolean; latencyMs: number; provider: StreamProvider }> {
  const t0 = Date.now()
  try {
    // Supabase: simple select latency check
    await supabase.from('operational_events').select('id').limit(1)
    return { ok: true, latencyMs: Date.now() - t0, provider: activeProvider }
  } catch {
    return { ok: false, latencyMs: Date.now() - t0, provider: activeProvider }
  }
}
